import { useCallback, useState } from 'react';

import { productionApi } from 'api/production';
import { applySliceChanges } from 'components/storefront/StorefrontConfigEditor/components/BulkApplyDialog/utils';
import { convertSlicesForPackGroup } from 'components/storefront/StorefrontConfigEditor/components/DuplicateSlicesDialog/utils';
import validateSlices from 'components/storefront/StorefrontConfigEditor/utils/validateSlices';

/**
 * Hook to manage the bulk apply save operation.
 * Handles saving the current config and applying changes to selected target configs.
 *
 * @returns {Object} Save state and handlers
 */
function useBulkApplySave() {
    const [saving, setSaving] = useState(false);
    const [saveStatuses, setSaveStatuses] = useState({});

    /**
     * Reset save statuses
     */
    const resetStatuses = useCallback(() => {
        setSaveStatuses({});
    }, []);

    /**
     * Execute the bulk apply operation.
     *
     * @param {Object} currentConfig - The current form data to save first
     * @param {boolean} includeProducts - Whether to include products in the copy
     * @param {Map<string, string>} originalSliceNames - Map of slice ID -> original name
     * @param {Set<string>} selectedConfigIds - IDs of configs selected for update
     * @param {Set<string>} selectedSliceIds - IDs of slices selected to apply
     * @param {Array} targetConfigs - Configs to apply changes to
     */
    const executeBulkApply = useCallback(
        async ({
            currentConfig,
            includeProducts,
            originalSliceNames,
            selectedConfigIds,
            selectedSliceIds,
            targetConfigs,
        }) => {
            setSaving(true);

            // Initialize all statuses - include current config and selected configs
            const initialStatuses = { [currentConfig.id]: 'saving' };
            selectedConfigIds.forEach((configId) => {
                initialStatuses[configId] = 'saving';
            });
            setSaveStatuses(initialStatuses);

            // First, save the current config
            try {
                await productionApi.saveStorefrontConfiguration(currentConfig, true);
                setSaveStatuses((prev) => ({ ...prev, [currentConfig.id]: 'success' }));
            } catch (error) {
                console.error(`Error saving current config ${currentConfig.id}:`, error);
                setSaveStatuses((prev) => ({ ...prev, [currentConfig.id]: 'error' }));
            }

            // Process only selected configs
            const configsToProcess = targetConfigs.filter((config) => selectedConfigIds.has(config.id));

            // eslint-disable-next-line no-restricted-syntax
            for (const config of configsToProcess) {
                try {
                    // Get the source slices that have matches in this config (by original name AND displayType)
                    // Only include slices that are selected by the user
                    const matchingSourceSlices = currentConfig.slices.filter((sourceSlice) => {
                        if (!selectedSliceIds.has(sourceSlice.id)) return false; // Skip unselected slices
                        const originalName = originalSliceNames.get(sourceSlice.id);
                        if (!originalName) return false; // Skip unnamed slices
                        // Must match both name AND displayType
                        return config.slices.some(
                            (targetSlice) =>
                                targetSlice.name === originalName &&
                                targetSlice.displayType === sourceSlice.displayType,
                        );
                    });

                    // Convert source slices' items for the target config's pack group
                    // eslint-disable-next-line no-await-in-loop
                    const convertedSourceSlices = await convertSlicesForPackGroup(
                        matchingSourceSlices,
                        config.packGroupID,
                    );

                    // Create a map of converted slices by ORIGINAL name + displayType for quick lookup
                    // This allows name changes to propagate: we match by original name, apply new name
                    const convertedByKey = new Map();
                    convertedSourceSlices.forEach((slice, index) => {
                        const originalName = originalSliceNames.get(matchingSourceSlices[index].id);
                        const { displayType } = matchingSourceSlices[index];
                        if (originalName) {
                            // Key is name + displayType to ensure type safety
                            convertedByKey.set(`${originalName}::${displayType}`, slice);
                        }
                    });

                    // Build updated slices array - match by target's current name AND displayType
                    const updatedSlices = config.slices.map((targetSlice) => {
                        const key = `${targetSlice.name}::${targetSlice.displayType}`;
                        const convertedSource = convertedByKey.get(key);
                        if (convertedSource) {
                            return applySliceChanges(targetSlice, convertedSource, includeProducts);
                        }
                        return targetSlice;
                    });

                    // Validate slices before saving to published configs
                    if (config.published) {
                        const validationErrors = validateSlices(updatedSlices);
                        if (validationErrors.length > 0) {
                            console.error(`Validation failed for published config ${config.id}:`, validationErrors);
                            setSaveStatuses((prev) => ({ ...prev, [config.id]: 'error' }));
                            // eslint-disable-next-line no-continue
                            continue;
                        }
                    }

                    // Save the updated config
                    // eslint-disable-next-line no-await-in-loop
                    await productionApi.saveStorefrontConfiguration({ ...config, slices: updatedSlices }, true);

                    setSaveStatuses((prev) => ({ ...prev, [config.id]: 'success' }));
                } catch (error) {
                    console.error(`Error saving config ${config.id}:`, error);
                    setSaveStatuses((prev) => ({ ...prev, [config.id]: 'error' }));
                }
            }

            setSaving(false);
        },
        [],
    );

    const allComplete =
        Object.values(saveStatuses).length > 0 &&
        Object.values(saveStatuses).every((s) => s === 'success' || s === 'error');

    const hasErrors = Object.values(saveStatuses).some((s) => s === 'error');

    return {
        saving,
        saveStatuses,
        resetStatuses,
        executeBulkApply,
        allComplete,
        hasErrors,
    };
}

export default useBulkApplySave;
